import { useState, useEffect, useCallback, useMemo } from 'react';
import { ref, onValue, remove } from 'firebase/database';
import { db } from '../config/firebase';
import type { Room, SessionEntry, ActivityLogEntry, PlayedGameEntry } from '../types';
import { formatDuration } from '../utils/formatTime';

export interface FlattenedSession extends SessionEntry {
  roomId: string;
  roomName: string;
}

export interface FlattenedActivity extends ActivityLogEntry {
  roomId: string;
  roomName: string;
}

export function useAdminData() {
  const [rooms, setRooms] = useState<Record<string, Room>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const roomsRef = ref(db, 'rooms');
    const unsubscribe = onValue(
      roomsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setRooms(snapshot.val() || {});
        } else {
          setRooms({});
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const deleteRoom = useCallback(async (roomId: string) => {
    if (!window.confirm('Es-tu sûr de vouloir supprimer définitivement cette room et toutes ses données ?')) {
      return;
    }
    await remove(ref(db, `rooms/${roomId}`));
  }, []);

  // Compute aggregated stats (memoized to prevent cascading re-renders)
  const { roomList, allSessions, allActivities, totalTrackedMs, totalTimersCount } = useMemo<{
    roomList: (Room & { id: string })[];
    allSessions: FlattenedSession[];
    allActivities: FlattenedActivity[];
    totalTrackedMs: number;
    totalTimersCount: number;
  }>(() => {
    const list: (Room & { id: string })[] = Object.entries(rooms).map(([id, room]) => ({
      ...room,
      id,
    }));

    const sessions: FlattenedSession[] = [];
    const activities: FlattenedActivity[] = [];
    let trackedMs = 0;
    let timersCount = 0;

    list.forEach((r) => {
      if (r.timers) {
        timersCount += Object.keys(r.timers).length;
      }

      if (r.history) {
        Object.entries(r.history).forEach(([sId, session]) => {
          sessions.push({
            ...session,
            id: sId,
            roomId: r.id,
            roomName: r.name || 'Salle sans nom',
          });
          trackedMs += session.totalDurationMs || 0;
        });
      }

      if (r.activityLog) {
        Object.entries(r.activityLog).forEach(([aId, act]) => {
          activities.push({
            ...act,
            id: aId,
            roomId: r.id,
            roomName: r.name || 'Salle sans nom',
          });
        });
      }
    });

    sessions.sort((a, b) => (b.endedAt || 0) - (a.endedAt || 0));
    activities.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    return {
      roomList: list,
      allSessions: sessions,
      allActivities: activities,
      totalTrackedMs: trackedMs,
      totalTimersCount: timersCount,
    };
  }, [rooms]);

  // Export functions
  const exportAsJSON = useCallback(() => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(rooms, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `namm-backup-${new Date().toISOString().slice(0, 10)}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  }, [rooms]);

  const exportAsCSV = useCallback(() => {
    const headers = [
      'Room ID',
      'Room Name',
      'Timer Name',
      'Type',
      'Duration (seconds)',
      'Duration (formatted)',
      'Stopped By',
      'Started At',
      'Ended At',
      'Games Played',
    ];

    const rows = allSessions.map((s) => {
      const games: PlayedGameEntry[] = Array.isArray(s.playedGames)
        ? s.playedGames
        : s.playedGames
        ? Object.values(s.playedGames as Record<string, PlayedGameEntry>)
        : [];
      const gamesStr = games
        .map((g) => `${g.game}${g.durationMs ? ` (${formatDuration(g.durationMs)})` : ''}`)
        .join('; ');

      return [
        `"${s.roomId}"`,
        `"${(s.roomName || '').replace(/"/g, '""')}"`,
        `"${(s.timerName || '').replace(/"/g, '""')}"`,
        `"${s.type || ''}"`,
        Math.round((s.totalDurationMs || 0) / 1000),
        `"${formatDuration(s.totalDurationMs || 0)}"`,
        `"${(s.stoppedBy || '').replace(/"/g, '""')}"`,
        `"${s.startedAt ? new Date(s.startedAt).toISOString() : ''}"`,
        `"${s.endedAt ? new Date(s.endedAt).toISOString() : ''}"`,
        `"${gamesStr.replace(/"/g, '""')}"`,
      ];
    });

    const csvContent =
      'data:text/csv;charset=utf-8,\uFEFF' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', encodeURI(csvContent));
    downloadAnchor.setAttribute('download', `namm-sessions-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  }, [allSessions]);

  return {
    rooms,
    roomList,
    allSessions,
    allActivities,
    totalTrackedMs,
    totalTimersCount,
    loading,
    error,
    deleteRoom,
    exportAsJSON,
    exportAsCSV,
  };
}
